// frontend/src/pages/CompBenchmarksPage.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { DecisionBand } from '../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const decisionBands: DecisionBand[] = [
  {"role":"Business Program Manager","company":"Google","min":190000,"max":210000},
  {"role":"Business Program Manager","company":"OpenAI","min":190000,"max":230000},
  {"role":"Business Program Manager","company":"Cloudflare","min":160000,"max":200000},
  {"role":"Data Engineer","company":"Google","min":200000,"max":255000},
  {"role":"Data Engineer","company":"OpenAI","min":270000,"max":340000},
  {"role":"Data Engineer","company":"Cloudflare","min":175000,"max":220000},
  {"role":"Application Engineer","company":"Google","min":200000,"max":230000},
  {"role":"Business Intelligence Engineer","company":"Google","min":200000,"max":230000},
  {"role":"Application Engineer","company":"OpenAI","min":280000,"max":340000},
  {"role":"Business Intelligence Engineer","company":"OpenAI","min":260000,"max":320000},
  {"role":"Application/BI Engineer","company":"Cloudflare","min":180000,"max":215000},
  {"role":"AI/ML Engineer","company":"Google","min":240000,"max":320000},
  {"role":"AI/ML Engineer","company":"OpenAI","min":320000,"max":400000},
  {"role":"AI/ML Engineer","company":"Cloudflare","min":190000,"max":230000},
  {"role":"Vendor Manager (Tech/Ops)","company":"Google","min":180000,"max":215000},
  {"role":"Vendor Manager (Tech/Ops)","company":"OpenAI","min":170000,"max":210000},
  {"role":"Vendor Manager (Tech/Ops)","company":"Cloudflare","min":145000,"max":190000}
];

const CONFIG = {
  currentBaseDefault: 165000,
  decisionBands,
};

const CompBenchmarksPage = () => {
  // const [activeTab, setActiveTab] = useState('ovw');
  
  // Overview state
  const [ovwRole, setOvwRole] = useState(CONFIG.decisionBands[0].role);
  const [ovwCompany, setOvwCompany] = useState(CONFIG.decisionBands[0].company);
  const [ovwBase, setOvwBase] = useState(CONFIG.currentBaseDefault);
  const [tenureYears, setTenureYears] = useState(12);
  const [docStrength, setDocStrength] = useState('Normal');

  // Overview calculated
  const [marketPosition, setMarketPosition] = useState('—');
  const [underpayDelta, setUnderpayDelta] = useState('—');
  const [underpayPct, setUnderpayPct] = useState('—');
  const [leverageScore, setLeverageScore] = useState('—');
  // const [bandChartData, setBandChartData] = useState<any>(null);

  // Benchmarks state
  const [benchSearch, setBenchSearch] = useState('');

  // Delta & Calculator state
  const [calcRole, setCalcRole] = useState(CONFIG.decisionBands[0].role);
  const [calcCompany, setCalcCompany] = useState(CONFIG.decisionBands[0].company);
  const [calcBase, setCalcBase] = useState(CONFIG.currentBaseDefault);
  const [targetPoint, setTargetPoint] = useState('mid');
  const [lookbackMonths, setLookbackMonths] = useState(12);
  const [benefitsPct, setBenefitsPct] = useState(15);
  // const [equityNote, setEquityNote] = useState('');

  // Delta calculated
  const [annualDelta, setAnnualDelta] = useState('—');
  const [backpay, setBackpay] = useState('—');
  const [totalWithBenefits, setTotalWithBenefits] = useState('—');
  // const [deltaDonutData, setDeltaDonutData] = useState<any>(null);

  // Attorney ROI state
  const [feeMode, setFeeMode] = useState('contingency');
  const [successPct, setSuccessPct] = useState(65);
  const [settlementMult, setSettlementMult] = useState(1.0);
  const [taxPct, setTaxPct] = useState(30);
  const [contPct, setContPct] = useState(33);
  const [feeShiftCont, setFeeShiftCont] = useState('No');
  const [hourlyRate, setHourlyRate] = useState(600);
  const [hours, setHours] = useState(120);
  const [feeShiftHr, setFeeShiftHr] = useState('No');

  // Attorney ROI calculated
  const [grossSettle, setGrossSettle] = useState('—');
  const [expectedValue, setExpectedValue] = useState('—');
  const [attorneyFees, setAttorneyFees] = useState('—');
  const [estTaxes, setEstTaxes] = useState('—');
  const [clientNet, setClientNet] = useState('—');
  const [attorneyROI, setAttorneyROI] = useState('—');
  
  // Sources state
  // const [internalNotes, setInternalNotes] = useState('');
  // const [externalUrls, setExternalUrls] = useState('');

  // AI Assist state
  // const [chatLog, setChatLog] = useState<{ role: string, text: string }[]>([]);
  // const [chatInput, setChatInput] = useState('');

  // const roles = [...new Set(CONFIG.decisionBands.map(x => x.role))];
  // const companies = [...new Set(CONFIG.decisionBands.map(x => x.company))];

  const fmt = (n: number | string) => isFinite(Number(n)) ? Number(n).toLocaleString('en-US', {style:'currency', currency:'USD', maximumFractionDigits:0}) : '—';

  const band = (role: string, company: string) => {
    return CONFIG.decisionBands.find(b => b.role === role && b.company === company);
  }
  const mid = (min: number, max: number) => (min + max) / 2;
  
  const recomputeOverview = useCallback(() => {
    const b = band(ovwRole, ovwCompany);
    if (!b) return;

    const base = ovwBase || 0;
    const midpoint = mid(b.min, b.max);
    
    let pos = 'Within Band';
    if (base < b.min) pos = 'Below Band';
    if (base > b.max) pos = 'Above Band';
    setMarketPosition(pos);

    const delta = Math.max(0, midpoint - base);
    setUnderpayDelta(fmt(delta));
    setUnderpayPct(delta > 0 ? `(${(delta / midpoint * 100).toFixed(1)}% to mid)` : '(at or above mid)');

    let score = 50;
    if (delta >= 75000) score += 30;
    else if (delta >= 50000) score += 20;
    else if (delta >= 25000) score += 10;
    if (tenureYears >= 10) score += 10;
    if (docStrength === 'Strong') score += 10;
    score = Math.max(20, Math.min(95, score));
    setLeverageScore(score.toString());

    /* setBandChartData({
        labels: ['Your Base', 'Band Min', 'Band Mid', 'Band Max'],
        datasets: [{
            data: [base, b.min, midpoint, b.max],
            backgroundColor: [
              'rgba(54, 162, 235, 0.6)',
              'rgba(255, 99, 132, 0.6)',
              'rgba(75, 192, 192, 0.6)',
              'rgba(255, 206, 86, 0.6)',
            ],
        }]
    }); */
  }, [ovwRole, ovwCompany, ovwBase, tenureYears, docStrength]);

  const deltaCalcBaseRef = useRef(0);

  const recomputeDelta = useCallback(() => {
    const b = band(calcRole, calcCompany);
    if (!b) return;

    const base = calcBase || 0;
    const midpoint = mid(b.min, b.max);
    let targetVal;
    if (targetPoint === 'min') targetVal = b.min;
    else if (targetPoint === 'max') targetVal = b.max;
    else targetVal = midpoint;

    const annualDeltaVal = Math.max(0, targetVal - base);
    const backpayVal = annualDeltaVal * (lookbackMonths / 12);
    const totalWithBenefitsVal = backpayVal * (1 + (benefitsPct / 100));
    
    deltaCalcBaseRef.current = totalWithBenefitsVal;

    setAnnualDelta(fmt(annualDeltaVal));
    setBackpay(fmt(backpayVal));
    setTotalWithBenefits(fmt(totalWithBenefitsVal));

    /* setDeltaDonutData({
        labels: ['Backpay', 'Benefits'],
        datasets: [{
            data: [backpayVal, totalWithBenefitsVal - backpayVal],
            backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 159, 64, 0.6)'],
        }]
    }); */
  }, [calcRole, calcCompany, calcBase, targetPoint, lookbackMonths, benefitsPct]);
  
  const recomputeAttorney = useCallback(() => {
    const baseDelta = deltaCalcBaseRef.current || 0;
    const p = (successPct / 100);
    const mult = settlementMult || 1.0;
    const gross = baseDelta * mult;
    const expected = gross * p;

    let fees = 0;
    const roi = '—'; // This is unused
    if (feeMode === 'contingency') {
        const pct = (contPct / 100);
        const shift = feeShiftCont === 'Yes';
        fees = shift ? 0 : expected * pct;
        // roi = `${Math.round(pct * 100)}% of settlement${shift ? ' (fee-shifted)' : ''}`;
    } else {
        const shift = feeShiftHr === 'Yes';
        fees = shift ? 0 : hourlyRate * hours;
        // roi = hours > 0 ? `${fmt(hourlyRate)} × ${hours} = ${fmt(hourlyRate * hours)}${shift ? ' (fee-shifted)' : ''}` : '—';
    }

    const estTaxesVal = expected * (taxPct / 100);
    const clientNetVal = Math.max(0, expected - fees - estTaxesVal);

    setGrossSettle(fmt(gross));
    setExpectedValue(fmt(expected));
    setAttorneyFees(fmt(fees));
    setEstTaxes(fmt(estTaxesVal));
    setClientNet(fmt(clientNetVal));
    setAttorneyROI(roi);
  }, [successPct, settlementMult, feeMode, contPct, feeShiftCont, feeShiftHr, hourlyRate, hours, taxPct]);


  useEffect(() => {
    recomputeOverview();
  }, [recomputeOverview]);

  useEffect(() => {
    recomputeDelta();
  }, [recomputeDelta]);
  
  useEffect(() => {
    recomputeAttorney();
  }, [recomputeAttorney, totalWithBenefits]);

  const filteredBands = CONFIG.decisionBands.filter(x => 
    (x.role + ' ' + x.company).toLowerCase().includes(benchSearch.toLowerCase())
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  
  /* const downloadCSV = (rows: string[][], filename: string) => {
    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }; */

  const copyBenchMD = () => {
    const headers = ['Role', 'Company', 'Base Range'];
    const data = filteredBands.map(b => [b.role, b.company, `${fmt(b.min)} – ${fmt(b.max)}`]);
    const markdown = [
      `| ${headers.join(' | ')} |`,
      `| ${headers.map(() => '---').join(' | ')} |`,
      ...data.map(row => `| ${row.join(' | ')} |`)
    ].join('\n');
    copyToClipboard(markdown);
  };
  
  const appendChat = (role: string, text: string) => {
    // setChatLog(prev => [...prev, { role, text }]);
  };
  
  const sendChatMessage = async (prompt: string) => {
    const payload = {
      messages: [
        {role:'system', content:'You are a legal-savvy comp analyst. Be concise, practical, and negotiation-oriented.'},
        {role:'user', content: prompt}
      ],
      context: {
        role: calcRole,
        company: calcCompany,
        currentBase: calcBase,
        bands: CONFIG.decisionBands,
        computed: {
          annualDelta: annualDelta,
          backpay: backpay,
          totalWithBenefits: totalWithBenefits,
          grossSettlement: grossSettle,
          expectedValue: expectedValue,
          clientNet: clientNet
        }
      }
    };
    appendChat('You', prompt);
    try {
      const res = await fetch('/api/ai/chat', {method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload)});
      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();
      appendChat('Assistant', data.reply || '(no reply)');
    } catch(e) {
      appendChat('Assistant', 'AI endpoint not available. Wire /api/ai/chat to your model.');
    }
  };

  return (
    <div>
      <h1>Compensation Benchmarks</h1>
      <p>Page under construction.</p>
    </div>
  );
};

export default CompBenchmarksPage;
